import type { Agent } from '~/core/entities/agent/Agent';
import type { Property } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { Dict } from 'mixpanel-browser';

import { sharingPlatforms } from '~/constants';

import tailwindBreakpoints from '../breakpoints.json';

// TODO: precisamos de testes para essa função
export function isEmpty(obj: Record<string, any> | any[] | Set<any>): boolean {
	if (Array.isArray(obj) && obj.length > 0) return false;
	if (obj instanceof Set && obj.size > 0) return false;
	if (typeof obj !== 'object') return true;
	for (const key in obj) if (Object.hasOwn(obj, key)) return false;
	return true;
}

function isObjectLike(value: unknown): value is object {
	return value !== null && typeof value === 'object';
}

function getTag(value: unknown): string {
	if (value === null) return '[object Null]';
	if (value === undefined) return '[object Undefined]';
	return Object.prototype.toString.call(value);
}

export function isNumber(value: unknown): value is number {
	// Matches lodash behavior: true for number primitives and Number objects
	// (including NaN and Infinity), false otherwise.
	return (
		typeof value === 'number' ||
		(isObjectLike(value) && getTag(value) === '[object Number]')
	);
}

export function baseURL(path: string, baseUrl: string) {
	return new URL(path, baseUrl).toString();
}

export function grammarInflection(
	number: number,
	singular: string,
	plural: string
) {
	return number === 1 ? singular : plural;
}

export function capitalize(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

export function dehyphenateString(value: string) {
	return value.split('-').map(capitalize).join(' ');
}

export function formatListWithConjunction(
	items: string[],
	conjunction: string = 'e'
) {
	if (items.length === 1) return items[0];
	if (items.length === 2) return items.join(` ${conjunction} `);

	const allButLast = items.slice(0, -1).join(', ');
	const lastItem = items[items.length - 1];
	return `${allButLast} ${conjunction} ${lastItem}`;
}

export function isMobileDevice(userAgent: NavigatorID['userAgent']): boolean {
	return /iPhone|iPad|iPod|iMob|Android|Mobi/i.test(userAgent || '');
}

function normalizeString(str: string, avoidChars: string[] = []): string {
	if (!str?.trim()) return '';

	const avoidCharsPattern = avoidChars.map(char => `\\${char}`).join('');

	return str
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(new RegExp(`[^a-z0-9${avoidCharsPattern}]`, 'g'), '');
}

/**
 * Normalizes a string by removing diacritics and converting to lowercase
 * @param str The string to normalize
 * @returns The normalized string
 * @example
 * normalizeStringBase('São Paulo') => 'sao paulo'
 * normalizeStringBase('Café') => 'cafe'
 */
export function normalizeStringBase(str: string): string {
	if (!str?.trim()) return '';
	return str
		.toLowerCase()
		.trim()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
}

export function normalizeStringForSearch(str: string) {
	if (!str?.trim()) return '';
	return str
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/\s+/g, '_');
}

export function slugify(str: string): string {
	return normalizeString(str, [' ', '-'])
		.replace(/(\s|-)+/g, '-')
		.replace(/^-|-$/g, '')
		.toLowerCase();
}

export const sanitize = (str: string) =>
	str.replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function getBoldMatchCaracteres(sentence = '', characters = '') {
	if (!characters) return sentence;

	const normalizedSentence = normalizeStringForSearch(sentence);
	const normalizedCharacters = normalizeStringForSearch(characters);

	const regEx = new RegExp(normalizedCharacters, 'gi');
	let match;
	let result = '';
	let lastIndex = 0;

	while ((match = regEx.exec(normalizedSentence)) !== null) {
		const startIndex = match.index;
		const endIndex = regEx.lastIndex;

		const originalMatch = sanitize(sentence.slice(startIndex, endIndex));

		result +=
			sentence.slice(lastIndex, startIndex) +
			`<strong class="font-semibold">${originalMatch}</strong>`;

		lastIndex = endIndex;
	}

	result += sanitize(sentence.slice(lastIndex));

	return result;
}

export interface HighlightPart {
	text: string;
	highlight: boolean;
}

/**
 * Splits a sentence into parts, marking which parts match the search characters.
 * This is a safe alternative to v-html for rendering highlighted search results.
 *
 * @param sentence - The full text to search within
 * @param characters - The search term to highlight
 * @returns Array of parts with text and highlight flag
 */
export function highlightParts(
	sentence = '',
	characters = ''
): HighlightPart[] {
	if (!characters) {
		return [{ text: sentence, highlight: false }];
	}

	const normalizedSentence = normalizeStringForSearch(sentence);
	const normalizedCharacters = normalizeStringForSearch(characters);

	const regEx = new RegExp(normalizedCharacters, 'gi');
	const parts: HighlightPart[] = [];
	let match;
	let lastIndex = 0;

	while ((match = regEx.exec(normalizedSentence)) !== null) {
		const startIndex = match.index;
		const endIndex = regEx.lastIndex;

		if (startIndex > lastIndex) {
			parts.push({
				text: sentence.slice(lastIndex, startIndex),
				highlight: false
			});
		}

		parts.push({
			text: sentence.slice(startIndex, endIndex),
			highlight: true
		});

		lastIndex = endIndex;
	}

	if (lastIndex < sentence.length) {
		parts.push({
			text: sentence.slice(lastIndex),
			highlight: false
		});
	}

	return parts.length > 0 ? parts : [{ text: sentence, highlight: false }];
}

export type ArrayOrWrapped<T> = T extends any[] ? T : Array<T>;

export function arraysAreEqual<T>(
	arr1: ArrayOrWrapped<T>,
	arr2: ArrayOrWrapped<T>
): boolean {
	return (
		arr1.length === arr2.length &&
		arr1.every((value, index) => value === arr2[index])
	);
}

// Helper to ensure value is an array
export const toArray = (val: any) =>
	Array.isArray(val) ? val : val ? [val] : [];

// Helper to get first element if array, or the value itself
export const getFirst = (val: any) =>
	Array.isArray(val) ? val[0] : val || undefined;

/** Atribui o mesmo valor a vários refs (útil em clear/reset de filtros). */
export function setRefsValue<T>(refs: { value: T }[], value: T): void {
	refs.forEach(r => {
		r.value = value;
	});
}

export function getPriceRangeLabel(
	min?: number,
	max?: number,
	labelIfEmpty = 'Valor'
) {
	if (!min && !max) return labelIfEmpty;

	const formattedMin = formatCurrency(min);
	const formattedMax = formatCurrency(max);

	if (min && !max) return `de ${formattedMin}`;
	if (!min && max) return `até ${formattedMax}`;

	return `${formattedMin} a ${formattedMax}`;
}

export function getAreaRangeLabel(
	min?: number,
	max?: number,
	labelIfEmpty = 'Área'
) {
	if (!min && !max) return labelIfEmpty;

	const formattedMin = floatToLocaleString(min);
	const formattedMax = floatToLocaleString(max);

	if (min && !max) return `de ${formattedMin}m²`;
	if (!min && max) return `até ${formattedMax}m²`;

	return `${formattedMin}m² a ${formattedMax}m²`;
}

export function getLocationPlaceholder({
	cities = [],
	regions = [],
	macroregions = [],
	states = [],
	addressSlug,
	separator = ' - ',
	includeRegion = false
}: {
	cities?: string[];
	regions?: string[];
	macroregions?: string[];
	states?: string[];
	addressSlug?: string;
	separator?: string;
	includeRegion?: boolean;
}) {
	if (addressSlug && addressSlug.includes(',')) {
		return addressSlug;
	}

	const region = includeRegion ? getFirst(regions) : undefined;
	const city = getFirst(cities) || getFirst(macroregions);
	const state =
		getFirst(states) || (cities?.length || macroregions?.length ? 'SP' : '');

	if (!city && !region) return '';

	if (region && city) {
		return `${region}, ${city}${state ? `${separator}${state}` : ''}`;
	}

	return `${city || region}${state ? `${separator}${state}` : ''}`.replaceAll(
		',',
		separator
	);
}

export function floatToLocaleString(
	floatCurrency: number = 0,
	config: Intl.NumberFormatOptions = {}
) {
	const floatCurrencyFixed = convertCurrencyValueToFloat(floatCurrency);
	const twoPlacedFloat = parseInt((floatCurrencyFixed * 100).toString()) / 100;
	const minMaxFractionDigits = Number.isInteger(floatCurrencyFixed) ? 0 : 2;
	return twoPlacedFloat.toLocaleString('pt-BR', {
		minimumFractionDigits: minMaxFractionDigits,
		maximumFractionDigits: minMaxFractionDigits,
		currency: 'BRL',
		...config
	});
}

export function formatCurrency(value?: number) {
	if (value === undefined) return '';

	if (value < 1000) {
		return floatToLocaleString(value, { style: 'currency' });
	}

	const units = [
		{ divisor: 1e9, suffix: 'B' },
		{ divisor: 1e6, suffix: 'M' },
		{ divisor: 1e3, suffix: 'K' }
	];

	for (const unit of units) {
		if (value >= unit.divisor) {
			const formattedValue = floatToLocaleString(value / unit.divisor, {
				style: 'currency'
			});
			return `${formattedValue} ${unit.suffix}`;
		}
	}

	return floatToLocaleString(value);
}

export type Breakpoint =
	| 'xs-min'
	| 'xs'
	| 'xs-large'
	| 'sm'
	| 'sm-large'
	| 'md'
	| 'md-large'
	| 'lg'
	| 'xl'
	| '2xl'
	| '3xl';

export const screenSizesInPixels: Record<Breakpoint, number> = Object.keys(
	tailwindBreakpoints
).reduce(
	(acc, breakpoint) => ({
		...acc,
		[breakpoint]: tailwindBreakpoints[breakpoint as Breakpoint]
	}),
	{} as Record<Breakpoint, number>
);
export function safeStringify<T>(
	value: T,
	fallbackValue: null | string = null
): string | null {
	try {
		return JSON.stringify(value);
	} catch (error) {
		console.error('Failed to stringify object:', error);
		return fallbackValue;
	}
}

const youtubeVideoUrlRegex =
	/https?:\/\/((www\.)?youtube\.com\/watch\?v=|youtu\.be\/|(www\.)?youtube\.com\/embed\/)(?<videoId>\w+)?((\?|&).+)?/;

export function isValidHttpUrl(string: string): boolean {
	try {
		const url = new URL(string);

		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

export function parseVideoUrl(url: string): string {
	const isValidUrl = isValidHttpUrl(url);

	if (!isValidUrl) return '';

	const isYoutubeUrl = url.includes('youtube') || url.includes('youtu.be');

	if (!isYoutubeUrl) return url;

	const matches = url.match(youtubeVideoUrlRegex);

	const videoId = matches?.groups?.videoId;

	return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
}

export function isGreaterThanZeroValue(value: number | undefined): boolean {
	return (value ?? 0) > 0;
}

export function isArrayDefinedAndNotEmpty<T = unknown>(
	array: T[] | undefined
): array is T[] {
	return (array?.length ?? 0) > 0;
}

export function safeAsyncTrack<T extends Dict>(event: string, params: T) {
	try {
		const { $mixpanel } = useNuxtApp();
		$mixpanel.asyncTrack(event, params);
	} catch (error) {
		// TODO: Implement error tracking
		// console.error(`Failed to track event ${event}:`, error);
	}
}

export function safeAsyncTrigger<T extends Dict>(event: string) {
	try {
		const { $mixpanel } = useNuxtApp();
		$mixpanel.asyncTrigger(event);
	} catch (error) {
		// TODO: Implement error tracking
		// console.error(`Failed to track event ${event}:`, error);
	}
}

export function safeTrack<T extends Dict>(event: string, params?: T) {
	try {
		const { $mixpanel } = useNuxtApp();
		$mixpanel.track(event, params);
	} catch (error) {
		// TODO: Implement error tracking
		// console.error(`Failed to track event ${event}:`, error);
	}
}

export const getChangedKeysFilters = (
	newObject: PropertyFilters,
	oldObject: PropertyFilters
): (keyof PropertyFilters)[] => {
	const changedKeys: (keyof PropertyFilters)[] = [];

	const keys = new Set<keyof PropertyFilters>([
		...(Object.keys(newObject) as (keyof PropertyFilters)[]),
		...(Object.keys(oldObject) as (keyof PropertyFilters)[])
	]);

	keys.forEach(key => {
		const newValue = newObject[key];
		const oldValue = oldObject[key];

		if (!newValue) return;

		if (Array.isArray(newValue) && Array.isArray(oldValue)) {
			if (
				newValue.length !== oldValue.length ||
				newValue.some((val, index) => val !== oldValue[index])
			) {
				changedKeys.push(key);
			}
		} else if (newValue !== oldValue) {
			changedKeys.push(key);
		}
	});

	return changedKeys;
};

export function isNotEmptyNorDuplicate(
	value: string,
	index: number,
	array: string[]
): boolean {
	return !!value.trim() && array.indexOf(value) === index;
}

export function minMaxString(min?: number, max?: number) {
	if (!min && !max) return '';

	if (!min || !max) return `${min || max}`;

	return `${min} - ${max}`;
}

export function getAgentFirstName(agent: Agent | null | undefined) {
	const name = agent?.display_name || agent?.name || '';
	return name.split(' ')[0] || '';
}

export function getAgentName(agent: Agent | null | undefined) {
	return agent?.display_name || agent?.name || '';
}

export function isNotEmptyAndHasNoZero(value: number[] | undefined) {
	if (!value || !value.length) return false;

	return value.length && !value.includes(0);
}

/**
 * Converts a string to camelCase
 * @param str The string to convert
 * @returns The converted string in camelCase
 * @example
 * toCamelCase('hello world') => 'helloWorld'
 * toCamelCase('hello_world') => 'helloWorld'
 * toCamelCase('hello-world') => 'helloWorld'
 * toCamelCase('HELLO_WORLD') => 'helloWorld'
 */
export function toCamelCase(str: string): string {
	if (!str) return '';

	return str
		.toLowerCase()
		.replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());
}

/**
 * Converts a string from CamelCase to snake_case
 * @param str The string to convert
 * @returns The converted string in snake_case
 * @example
 * camelToSnake('helloWorld') => 'hello_world'
 * camelToSnake('HelloWorld') => 'hello_world'
 */
export function camelToSnake(str: string): string {
	return str
		.replace(/([A-Z])/g, '_$1')
		.toLowerCase()
		.replace(/^_/, '');
}

type SnakeCase<T extends string> = T extends `${infer First}${infer Rest}`
	? First extends Uppercase<First>
		? `_${Lowercase<First>}${SnakeCase<Rest>}`
		: `${First}${SnakeCase<Rest>}`
	: T;

type SnakeCaseObject<T> = {
	[K in keyof T as K extends string ? SnakeCase<K> : never]: T[K];
};

type CamelCase<T extends string> = T extends `${infer First}_${infer Rest}`
	? `${First}${Capitalize<CamelCase<Rest>>}`
	: T;

type CamelCaseObject<T> = {
	[K in keyof T as K extends string ? CamelCase<K> : never]: T[K];
};

/**
 * Converts an object's keys from CamelCase to snake_case
 * @param obj The object to convert
 * @returns A new object with snake_case keys
 * @example
 * camelToSnakeObject({ helloWorld: 'value' }) => { hello_world: 'value' }
 */
export function camelToSnakeObject<T extends Record<string, any>>(
	obj: T
): SnakeCaseObject<T> {
	const result = {} as SnakeCaseObject<T>;

	Object.entries(obj).forEach(([key, value]) => {
		const snakeKey = camelToSnake(key) as keyof SnakeCaseObject<T>;
		result[snakeKey] = value;
	});

	return result;
}

/**
 * Converts an object's keys from snake_case to camelCase
 * @param obj The object to convert
 * @returns A new object with camelCase keys
 * @example
 * snakeToCamelObject({ hello_world: 'value' }) => { helloWorld: 'value' }
 */
export function snakeToCamelObject<T extends Record<string, any>>(
	obj: T
): CamelCaseObject<T> {
	const result = {} as CamelCaseObject<T>;

	Object.entries(obj).forEach(([key, value]) => {
		const camelKey = toCamelCase(key) as keyof CamelCaseObject<T>;
		result[camelKey] = value;
	});

	return result;
}

/**
 * Generates a location slug from a property, for example: pinheiros-sao-paulo-sp-brasil or sao-paulo-sp-brasil
 *
 * @param property - The property to generate the location slug from
 * @returns The location slug
 */
export function getLocationSlugFromProperty(property: Property | null): string {
	if (!property) return '';
	const defaultLocationSlug = 'sao-paulo-sp-brasil';

	const locationSlugParts = [property.region, property.city, property.state]
		.filter(Boolean)
		.map(slugify);

	const locationSlug = locationSlugParts.join('')
		? [...locationSlugParts, 'brasil'].join('-')
		: defaultLocationSlug;

	return locationSlug;
}

/**
 * Creates sharing URLs optimized for sharing messages with text content
 * @param encodedMessage - The encoded message to share
 * @param subject - Optional subject for email sharing
 * @returns Object with URLs for all platforms optimized for message sharing
 */
export function createSharingUrlsForMessage(
	encodedMessage: string,
	subject?: string
) {
	return {
		whatsapp: sharingPlatforms.whatsapp(encodedMessage),
		twitter: sharingPlatforms.twitter.withText(encodedMessage),
		facebook: sharingPlatforms.facebook.withQuote(encodedMessage),
		email: sharingPlatforms.email(encodedMessage, subject || '')
	};
}

/**
 * Creates sharing URLs optimized for sharing URLs/links
 * @param encodedUrl - The encoded URL to share
 * @param subject - Optional subject for email sharing
 * @returns Object with URLs for all platforms optimized for URL sharing
 */
export function createSharingUrlsForUrl(encodedUrl: string, subject?: string) {
	return {
		whatsapp: sharingPlatforms.whatsapp(encodedUrl),
		twitter: sharingPlatforms.twitter.withUrl(encodedUrl),
		facebook: sharingPlatforms.facebook.withUrl(encodedUrl),
		email: sharingPlatforms.email(encodedUrl, subject || '')
	};
}

// Animation utilities
export {
	createCancellableDelay,
	createAnimationManager,
	useAnimationManager
} from './animation';

// Session utilities
export {
	calculateSessionDuration,
	calculateSessionDurationFromSession
} from './session';

/**
 * Creates property filters from user preferences with smart price range calculation
 * @param preferences - User preferences from cookie
 * @returns Property filters object or null if no relevant preferences
 */
export function createFiltersFromPreferences(preferences: any) {
	if (
		!preferences?.regions?.length &&
		!preferences?.cities?.length &&
		!preferences?.askingPrice
	) {
		return null;
	}

	const priceRange = preferences.askingPrice
		? createPriceRangeFilter(preferences.askingPrice)
		: {};

	return {
		regions: [...(preferences.regions || [])],
		cities: [...(preferences.cities || [])],
		propertyTypes: [...(preferences.propertyTypes || [])],
		...priceRange
	};
}

/**
 * Creates a price range filter based on a reference price with 20% margin
 * @param referencePrice - The reference price to create range from
 * @returns Object with minPrice and maxPrice (20% below and above reference)
 */
export function createPriceRangeFilter(referencePrice: number) {
	return {
		minAskingPrice: Math.floor(referencePrice * 0.8), // 20% lower
		maxAskingPrice: Math.ceil(referencePrice * 1.2) // 20% higher
	};
}

export function getCityFromAddressSlug(
	addressSlug: string | undefined
): string {
	if (!addressSlug) return '';

	const parts = addressSlug.split(',');
	const lastPart = parts[parts.length - 1]?.trim() || '';

	if (lastPart.includes('-')) {
		return lastPart.split('-')[0]?.trim() || '';
	}

	return '';
}
